import React, { useMemo } from 'react';
import { Menu, Button, Badge, Dropdown, List, Typography } from 'antd';
import { UserOutlined, BellOutlined, CheckOutlined, EllipsisOutlined, PoweroffOutlined, SettingOutlined, ClusterOutlined, AppstoreOutlined, ApartmentOutlined, MenuFoldOutlined, CloseOutlined } from '@ant-design/icons';
import './Navbar.css';
import RouteMap from './RouteMap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/use_subscription';
import { EntityImage } from '../components/Entities/EntityImage';
import { useNotifications } from '../hooks/use_notifications';
import { useAuth } from '../context/AuthContext';
import { useNodeInfo } from '../hooks/use_node_info';
import { useNodeApps } from '../hooks/use_node_apps';
import { useGatheringStatus } from '../hooks/use_gathering';
import NodeSwitcher from '../components/Node/NodeSwitcher';
import NodeLogo from '../components/Node/NodeLogo';
import { useSuperAdmins } from '../hooks/use_super_admins';
import { useIsMobile } from '../hooks/use_is_mobile';
import { useSidebarPins } from '../hooks/use_sidebar_pins';
import { edgeLabel } from '../utils/connections';

// Where a pinned entity opens: an app instance has its own page.
const entityPath = entity => (entity.entity_type === 'app' ? `/app/${entity.id}` : `/entity/${entity.id}`);

function NavBar({ collapsed = false, onToggleCollapsed }) {
    const { is_subscriber } = useSubscription();
    const { unreadCount, unreadMessagesTotal, unreadByApp, notifications, dropdownOpen, openDropdown, closeDropdown, markAllRead, featureNotifications } = useNotifications();
    const { userPersonEntity } = useAuth();
    const { nodeName, featureMessaging, featureGathering, connectionsEnabled, federationEnabled } = useNodeInfo();
    // Shown beside the logo and in the node settings entry: "Warmdata".
    const displayNodeName = nodeName ? `${nodeName.charAt(0).toUpperCase()}${nodeName.slice(1)}` : null;
    // 'waiting' or 'open' means members have somewhere to be right now —
    // worth a "live" badge on the nav entry even before they click in.
    const gatheringStatus = useGatheringStatus({ enabled: featureGathering });
    const gatheringLive = gatheringStatus === 'waiting' || gatheringStatus === 'open';
    const { isServerAdmin } = useSuperAdmins();
    // Apps a superadmin installed in the node itself (node settings → Apps):
    // for everyone signed in here, so each gets a nav entry.
    const { apps: nodeApps } = useNodeApps();
    // What this person pinned to their own sidebar (the pin button on an
    // entity's or a connection's page): drawn after the built-in pages.
    const { pins, unpin } = useSidebarPins();
    const navigate = useNavigate();
    const location = useLocation();
    // A sidebar down the left on wide screens; on a phone it would eat too
    // much of the width, so there it stays a bar across the top.
    const isMobile = useIsMobile();
    const menuMode = isMobile ? 'horizontal' : 'inline';
    // Collapsing only applies to the sidebar: the phone's top bar has no room
    // to give back. Collapsed, the rail holds the logo alone, which expands it.
    const isCollapsed = !isMobile && collapsed;
    const menuStyle = isMobile
        ? { background: 'transparent', borderBottom: 'none' }
        : { background: 'transparent', borderInlineEnd: 'none' };
    // The actions sit at the foot of the sidebar, so their dropdowns open upward.
    const actionsPlacement = isMobile ? 'bottomRight' : 'topLeft';

    const selectedNavKey = useMemo(() => {
        const navPaths = RouteMap
            .filter(route => route.placeinNavBar && route.path !== '/logout')
            .map(route => route.path);
        const match = navPaths
            .filter(p => location.pathname === p || location.pathname.startsWith(p + '/'))
            .sort((a, b) => b.length - a.length)[0];
        return match ? [match] : [location.pathname];
    }, [location.pathname]);

    const navItems = useMemo(() => {
        return RouteMap
            .filter(route => route.placeinNavBar && route.path !== '/logout')
            // Tier 2 gate (#36): hide the Messages entry on nodes without messaging.
            .filter(route => featureMessaging || route.path !== '/messages')
            // Node feature toggle: hide Gathering on nodes without it
            // (feature_gathering).
            .filter(route => featureGathering || route.path !== '/gathering')
            // No context or relationship type on this node means no
            // connections to list (see connectionsEnabled in use_node_info.js).
            .filter(route => connectionsEnabled || route.path !== '/connections')
            // Nothing is shared by peers on a node that has none (federation_enabled).
            .filter(route => federationEnabled || route.path !== '/shared')
            .map(route => {
                let icon = route.icon;
                const label = route.name;
                if (route.path === '/messages') {
                    // Badge lives on the icon (not the label) so the unread count
                    // still shows up on mobile, where the label text is hidden.
                    icon = (
                        <Badge count={unreadMessagesTotal} size="small" offset={[4, -2]}>
                            {icon}
                        </Badge>
                    );
                }
                if (route.path === '/gathering' && gatheringLive) {
                    // A plain dot, not a count — there's nothing to count,
                    // just "there's somewhere to be right now".
                    icon = (
                        <Badge dot color="var(--status-success)" offset={[4, -2]}>
                            {icon}
                        </Badge>
                    );
                }
                return { label, key: route.path, icon };
            });
    }, [unreadMessagesTotal, featureMessaging, featureGathering, connectionsEnabled, federationEnabled, gatheringLive]);
    // One entry per app installed in the node, after the built-in pages. Its
    // key is the app's route, which selectedNavKey falls back to, so the entry
    // highlights while that app is open.
    // An app's unread notifications (kind=app, keyed by the instance) badge
    // its entry, as unread messages badge Messages.
    const nodeAppItems = useMemo(
        () => nodeApps.map(app => ({
            label: app.name,
            key: `/app/${app.id}`,
            icon: (
                <Badge count={unreadByApp[app.id] || 0} size="small" offset={[4, -2]}>
                    <AppstoreOutlined />
                </Badge>
            ),
        })),
        [nodeApps, unreadByApp],
    );
    // One entry per pin. An entity shows its picture and name; a connection
    // the two names, person first, the way a membership is said. The × takes
    // the pin off again without opening it — it stops the click before the
    // menu item (and the dropdown item on a phone) can navigate.
    const pinnedItems = useMemo(() => pins.map(pin => {
        const target = pin.entity
            ? { key: entityPath(pin.entity), name: pin.entity.name, icon: <EntityImage entity={pin.entity} size={16} className="navbar-pin-avatar" /> }
            : { key: `/connection/${pin.connection.id}`, name: edgeLabel({ a: pin.connection.entity_a, b: pin.connection.entity_b }).name, icon: <ApartmentOutlined /> };
        return {
            key: target.key,
            icon: target.icon,
            title: target.name,
            label: (
                <span className="navbar-pin">
                    <span className="navbar-pin-name">{target.name}</span>
                    <button
                        type="button"
                        className="navbar-pin-remove"
                        title="Remove from sidebar"
                        aria-label={`Remove ${target.name} from sidebar`}
                        onClick={e => { e.stopPropagation(); unpin(pin.id).catch(() => {}); }}
                    >
                        <CloseOutlined />
                    </button>
                </span>
            ),
        };
    }), [pins, unpin]);
    // Gathering sits apart, just before "me" (on the right, or at the foot of
    // the sidebar); the rest run from the logo. The pins come last, under
    // their own heading — in the sidebar only: the phone's top bar has no
    // room for them, so there they live in the ⋯ menu instead.
    const mainNavItems = [
        ...navItems.filter(item => item.key !== '/gathering'),
        ...nodeAppItems,
        ...(!isMobile && pinnedItems.length > 0
            ? [{ type: 'group', key: 'pinned', label: 'Pinned', className: 'navbar-pinned-group', children: pinnedItems }]
            : []),
    ];
    const endNavItems = navItems.filter(item => item.key === '/gathering');

    const moreItems = [
        ...(isMobile && pinnedItems.length > 0 ? [
            {
                type: 'group',
                key: 'pinned',
                label: 'Pinned',
                className: 'navbar-pinned-group',
                children: pinnedItems.map(item => ({ ...item, onClick: () => navigate(item.key) })),
            },
            { type: 'divider' },
        ] : []),
        {
            key: '/account',
            label: 'Account',
            icon: <SettingOutlined />,
            onClick: () => navigate('/account'),
        },
        {
            // What the node offers: the catalog, and the apps installed in the
            // node (AppsPage.jsx). Everyone signed in may look.
            key: '/apps',
            label: 'Apps',
            icon: <AppstoreOutlined />,
            onClick: () => navigate('/apps'),
        },
        ...(isServerAdmin ? [{
            key: '/node-account',
            // Same wording as the page's title: "Warmdata settings".
            label: displayNodeName ? `${displayNodeName} settings` : 'Node settings',
            icon: <ClusterOutlined />,
            onClick: () => navigate('/node-account'),
        }] : []),
        { type: 'divider' },
        {
            key: '/logout',
            label: 'Logout',
            icon: <PoweroffOutlined />,
            danger: true,
            onClick: () => navigate('/logout'),
        },
    ];

    // Where a notification leads, by kind: only some have somewhere to land.
    // A message or reaction opens its channel, an app's notification opens
    // the app (which marks it read), a join request opens the entity it is
    // for, and a signup lands on the home page, where admins review the
    // queue. A registration decision is news with nowhere to go.
    const destinationFor = item => {
        switch (item.kind) {
            case 'message':
            case 'reaction':
                return `/messages/${item.entity_id}`;
            case 'app':
                return `/app/${item.entity_id}`;
            case 'join_request':
                return `/entity/${item.entity_id}`;
            case 'signup_pending':
            case 'user_registered':
                return '/';
            default:
                return null;
        }
    };
    const openNotification = item => {
        const to = destinationFor(item);
        if (!to) return;
        closeDropdown();
        navigate(to);
    };

    const notificationDropdown = (
        <div className="notif-panel">
            <div className="notif-panel-header">
                <Typography.Text strong style={{ fontSize: 13 }}>Notifications</Typography.Text>
                {unreadCount > 0 && (
                    <Button type="text" size="small" icon={<CheckOutlined />} onClick={markAllRead} style={{ fontSize: 11 }}>
                        Mark all read
                    </Button>
                )}
            </div>
            <List
                style={{ maxHeight: 360, overflowY: 'auto' }}
                dataSource={notifications}
                locale={{ emptyText: 'No notifications' }}
                renderItem={item => (
                    <List.Item
                        className={`notif-item${item.is_read ? '' : ' notif-item--unread'}${destinationFor(item) ? ' notif-item--link' : ''}`}
                        onClick={() => openNotification(item)}
                    >
                        <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                <Typography.Text strong style={{ fontSize: 12 }}>{item.sender_name || 'Someone'}</Typography.Text>
                                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                                    {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography.Text>
                            </div>
                            <Typography.Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
                                {item.preview}
                            </Typography.Text>
                        </div>
                    </List.Item>
                )}
            />
        </div>
    );

    if (isCollapsed) {
        return (
            <header className="vellby-navbar vellby-navbar--vertical vellby-navbar--collapsed">
                <button
                    className="navbar-icon-btn navbar-expand-btn"
                    title="Expand sidebar"
                    aria-label="Expand sidebar"
                    aria-expanded={false}
                    onClick={onToggleCollapsed}
                >
                    <NodeLogo size={24} />
                </button>
            </header>
        );
    }

    return (
        <header className={`vellby-navbar${isMobile ? '' : ' vellby-navbar--vertical'}`}>
            <div className="navbar-brand-row">
                {/* The logo and name double as the node switcher for people who
                    belong to several nodes (or may create one); otherwise they
                    just go home. */}
                <NodeSwitcher
                    size={20}
                    className="navbar-logo"
                    onHome={() => navigate('/')}
                    label={displayNodeName && <span className="navbar-node-name">{displayNodeName}</span>}
                />
            </div>
            <Menu
                id="tour-navbar"
                mode={menuMode}
                selectedKeys={selectedNavKey}
                onClick={(e) => navigate(e.key)}
                className="navbar-menu"
                style={{ ...menuStyle, flex: 1 }}
                items={mainNavItems}
                disabledOverflow={true}
            />
            {endNavItems.length > 0 && (
                <Menu
                    mode={menuMode}
                    selectedKeys={selectedNavKey}
                    onClick={(e) => navigate(e.key)}
                    className="navbar-menu"
                    style={menuStyle}
                    items={endNavItems}
                    disabledOverflow={true}
                />
            )}
            <div className="navbar-actions">
                {/* The person's own picture stands in for "me": uploaded or
                    generated, same as everywhere else. Subscribers keep their
                    gold mark as a ring around it rather than a tinted icon. */}
                <button
                    className={`navbar-icon-btn navbar-me-btn${userPersonEntity && location.pathname === `/entity/${userPersonEntity.id}` ? ' navbar-me-btn--active' : ''}${is_subscriber ? ' navbar-me-btn--subscriber' : ''}`}
                    title={userPersonEntity?.name || 'Me'}
                    aria-label={userPersonEntity?.name || 'Me'}
                    disabled={!userPersonEntity}
                    onClick={() => userPersonEntity && navigate(`/entity/${userPersonEntity.id}`)}
                >
                    {userPersonEntity
                        ? <EntityImage entity={userPersonEntity} size={22} className="navbar-me-avatar" />
                        : <UserOutlined />}
                </button>
                {featureNotifications && (
                    <Dropdown
                        open={dropdownOpen}
                        onOpenChange={(open) => { if (open) openDropdown(); else closeDropdown(); }}
                        dropdownRender={() => notificationDropdown}
                        trigger={['click']}
                        placement={actionsPlacement}
                    >
                        <button className="navbar-icon-btn">
                            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                                <BellOutlined />
                            </Badge>
                        </button>
                    </Dropdown>
                )}
                <Dropdown menu={{ items: moreItems }} trigger={['click']} placement={actionsPlacement}>
                    <button className="navbar-icon-btn">
                        <EllipsisOutlined style={{ transform: 'rotate(90deg)' }} />
                    </button>
                </Dropdown>
                {!isMobile && onToggleCollapsed && (
                    <button
                        className="navbar-icon-btn navbar-collapse-btn"
                        title="Collapse sidebar"
                        aria-label="Collapse sidebar"
                        aria-expanded={true}
                        onClick={onToggleCollapsed}
                    >
                        <MenuFoldOutlined />
                    </button>
                )}
            </div>
        </header>
    );
}

export default NavBar;
